import { Component, DestroyRef, OnInit, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { API, ChannelFullDTO, Order, ResolutionDTO, ss_nm_Channel_retrieveAll_order, ThumbnailSize } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { ChannelFullDTOAndTags } from '../../../models/taggable.model';
import { NavigationService } from '@ngx-seatback/utils';
import { OrderOption, SbSplitButton, SplitButtonClass } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Resolution } from '../../../services/api/api-resolution';
import { RequestClass } from '../../../services/global/RequestClass';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'edit';
type ChannelFullDTOAndTagsAndButtons = SplitButtonClass<ChannelFullDTOAndTags, SplitButtonAction>;

@Component({
  selector: 'sb-page-channels',
  templateUrl: './channels.component.html',
  styleUrls: ['./channels.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelsComponent extends RequestClass implements OnInit {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  public readonly tagService = inject(TagService);
  public readonly permissionService = inject(PermissionService);
  protected readonly ThumbnailSize = ThumbnailSize;
  protected readonly ss_nm_Channel_retrieveAll_order = ss_nm_Channel_retrieveAll_order;
  protected readonly api = inject(API);
  private readonly navService = inject(NavigationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  channels: ChannelFullDTOAndTagsAndButtons[] = [];

  // filter variables
  hasDevices: boolean | null = null;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };
  resolution: ResolutionDTO;
  mediaResolutions: ResolutionDTO[];
  allOrderOptions: OrderOption<ss_nm_Channel_retrieveAll_order>[] = [
    {
      value: ss_nm_Channel_retrieveAll_order.CLIENT_ORDER_NUM,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.clientOrderNum'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.ID,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.id'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.NAME,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.name'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.MARKER_WIDTH,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.markerWidth'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.MARKER_HEIGHT,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.markerHeight'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.COUNT_DEVICES,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.devices'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.COUNT_DEVICES_PRODUCTIVE,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.devicesProductive'),
    },
    {
      value: ss_nm_Channel_retrieveAll_order.CREATED_AT,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.createdAt'),
    },
  ] as const;
  orderOption = signal<OrderOption<ss_nm_Channel_retrieveAll_order>>(this.allOrderOptions[0]);

  ngOnInit() {
    this.resetOrderOption();

    this.api
      .ss_nm_Channel_retrieveAllResolutions()
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: ResolutionDTO[]) => {
          this.mediaResolutions = data;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ss_nm_Channel_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          hasDevices: this.hasDevices,
          // resolution: res ? [res] : null,
          width: this.resolution?.width ? [this.resolution.width] : null,
          height: this.resolution?.height ? [this.resolution.height] : null,
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(channels => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Channel, channels)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (receivedChannels: ChannelFullDTOAndTags[]) => {
          this.channels = this.buildSplitButtons(receivedChannels);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  createChannel() {
    const newChannel: ChannelFullDTO = this.api.createDTO('ChannelFullDTO');
    newChannel.name = 'New Channel';
    this.api
      .ss_nm_Channel_create(newChannel)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (createdChannel: ChannelFullDTO) => {
          this.navService.navigateToPoint(this.util.RoutingID.channelDetails, [
            {
              id: 'channelID',
              value: createdChannel.id,
            },
          ]);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  getOrder(): { by: ss_nm_Channel_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
      return {
        by: ss_nm_Channel_retrieveAll_order.CLIENT_ORDER_NUM,
        direction: Order.desc,
      };
    }
    // if value equals column title
    switch (currentOrderOption.value) {
      case ss_nm_Channel_retrieveAll_order.NAME:
        return { by: ss_nm_Channel_retrieveAll_order.NAME, direction: currentOrderOption.direction };
      case ss_nm_Channel_retrieveAll_order.ID:
        return { by: ss_nm_Channel_retrieveAll_order.ID, direction: currentOrderOption.direction };
      case ss_nm_Channel_retrieveAll_order.CREATED_AT:
        return { by: ss_nm_Channel_retrieveAll_order.CREATED_AT, direction: currentOrderOption.direction };
      default:
        return { by: currentOrderOption.value, direction: currentOrderOption.direction };
    }
  }

  setToOrderAndRefresh(pValue: ss_nm_Channel_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: ss_nm_Channel_retrieveAll_order) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...this.allOrderOptions.find(option => option.value === pValue) });
    }
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ value: ss_nm_Channel_retrieveAll_order.CLIENT_ORDER_NUM, direction: Order.desc });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (
        this.permissionService.hasPermissions([
          this.permissionService.Permission.channel_show,
          this.permissionService.Permission.channel_list,
        ])
      ) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.channelDetails,
          pathParams: [{ id: 'channelID', value: item?.id }],
        });
      }
    });
    return result;
  }

  private setOrderOptionFromPanel(value: ss_nm_Channel_retrieveAll_order, pDirection: Order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === value);
    if (!foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    this.orderOption.set({ ...foundOrderOption, direction: pDirection });
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.resolution = filterMap.get('resolution')?.[0];
    this.hasDevices = filterMap.get('hasDevices')?.[0];
    const orderOption = filterMap.get('orderOption')?.[0];
    const orderDirection = filterMap.get('orderDirection')?.[0];
    this.setOrderOptionFromPanel(orderOption, orderDirection);
  }

  protected identity(data: any): ChannelFullDTOAndTagsAndButtons {
    return data;
  }
}
